// src/diagnostics/nxrEngine.js
// Motore della diagnostica dei tracker: dalle righe di log della seriale allo
// stato di ogni controllo. Funzioni pure (nessun accesso a seriale o pagina):
// le regole stanno in nxrRules.js.
//
//   evaluate(lines)              stato durante il test (controlli "in attesa")
//   finalize(ev, { reason, led }) esito finale: i controlli rimasti senza
//                                risposta diventano guasti o non verificabili
//   LineSplitter                 spezza il flusso di testo in righe (\n, senza \r)

import { NXR_RULES, CHECKS, fmtNumber } from './nxrRules';

const R = NXR_RULES;
const C = R.common;

export const STATUS_LABEL = {
  pending: 'In attesa',
  ok: 'OK',
  fail: 'Guasto',
  warn: 'Da verificare',
  na: 'Non verificabile',
};

const DEP_REASON = {
  modem: 'il modem non risponde',
  sim: 'la SIM non è pronta',
  network: 'manca la rete cellulare',
  battery: 'il lettore batteria non risponde',
};

const LABEL = Object.fromEntries(CHECKS.map(c => [c.id, c.label]));

/** Flusso di testo → righe complete (la riga a metà resta in attesa). */
export class LineSplitter {
  constructor(maxLine = 4000) { this.buf = ''; this.maxLine = maxLine; }
  push(text) {
    this.buf += text;
    const parts = this.buf.split('\n');
    this.buf = parts.pop();
    // una riga senza fine (dati illeggibili) non deve crescere all'infinito
    if (this.buf.length > this.maxLine) { parts.push(this.buf); this.buf = ''; }
    return parts.map(l => l.replace(/\r/g, ''));
  }
  flush() { const rest = this.buf.replace(/\r/g, ''); this.buf = ''; return rest ? [rest] : []; }
}

function matchFirst(rules, line) {
  if (!rules) return null;
  for (const rule of rules) {
    const m = rule.re.exec(line);
    if (m) return { rule, m };
  }
  return null;
}

/** Indice della riga da cui parte l'ultimo avvio (banner), -1 se non visto. */
export function bootStartIndex(lines) {
  for (let i = lines.length - 1; i >= 0; i--) {
    if (C.banner.test(lines[i])) return i > 0 && C.bannerTop.test(lines[i - 1]) ? i - 1 : i;
  }
  return -1;
}

/** Controllo a regole: vale l'ultimo esito che compare. */
function evalRuleCheck(def, lines, from) {
  const st = { status: 'pending', msg: '', detail: '', values: null, at: -1, steps: 0 };
  if (!def) return st;
  for (let i = from; i < lines.length; i++) {
    const line = lines[i];
    const f = matchFirst(def.fail, line);
    if (f) {
      Object.assign(st, { status: def.failAs || 'fail', msg: f.rule.msg || '', detail: '', values: null, at: i, steps: 0 });
      continue;
    }
    const w = matchFirst(def.warn, line);
    if (w) {
      Object.assign(st, { status: 'warn', msg: w.rule.msg || '', at: i });
      continue;
    }
    if (def.steps) {
      // un passo successivo vale anche per i precedenti (CGATT OK = rete registrata)
      for (let j = def.steps.length - 1; j >= 0; j--) {
        const m = def.steps[j].re.exec(line);
        if (!m) continue;
        if (def.steps[j].detail) st.detail = def.steps[j].detail(m);
        if (j + 1 === def.steps.length) Object.assign(st, { status: 'ok', msg: '', at: i, steps: 0 });
        else if (st.status !== 'ok') Object.assign(st, { status: 'pending', msg: '', steps: Math.max(st.steps, j + 1) });   // tentativo in corso
        break;
      }
      continue;
    }
    const o = matchFirst(def.ok, line);
    if (o) {
      Object.assign(st, {
        status: 'ok', msg: '', at: i,
        detail: o.rule.detail ? o.rule.detail(o.m) : st.detail,
        values: o.rule.values ? o.rule.values(o.m) : st.values,
      });
    }
  }
  return st;
}

function resolveProfile(lines, from, model) {
  if (model && R.profiles.includes(model)) return { id: model, source: 'banner' };
  let best = null;
  for (const id of R.profiles) {
    let hits = 0;
    for (let i = Math.max(0, from); i < lines.length; i++) if (R[id].signature.some(re => re.test(lines[i]))) hits++;
    if (hits && (!best || hits > best.hits)) best = { id, hits };
  }
  return best ? { id: best.id, source: 'log' } : { id: null, source: null };
}

/** Bus I2C: indirizzi trovati e, a scansione finita, quelli che mancano. */
function evalI2C(lines, from, profileId) {
  const st = { status: 'pending', msg: '', detail: '', at: -1 };
  const fail = evalRuleCheck(C.checks.i2c, lines, from);
  if (fail.status !== 'pending') return { ...st, ...fail };
  const found = new Set();
  let scanStarted = false;
  let scanEnded = false;
  for (let i = from; i < lines.length; i++) {
    const m = C.i2cFound.exec(lines[i]);
    if (m) { found.add(parseInt(m[1], 16)); scanStarted = true; continue; }
    if (/\[I2C\] Scan bus/.test(lines[i])) scanStarted = true;
    if (scanStarted && C.i2cScanEnd.test(lines[i])) { scanEnded = true; st.at = i; break; }
  }
  const hex = (a) => `0x${a.toString(16).toUpperCase().padStart(2, '0')}`;
  const list = [...found].sort((a, b) => a - b).map(hex).join(', ');
  const candidates = profileId ? [profileId] : R.profiles;
  let bestMissing = null;
  for (const id of candidates) {
    const missing = R[id].i2cRequired.filter(g => !g.any.some(a => found.has(a))).map(g => g.label);
    if (!missing.length) return { ...st, status: 'ok', detail: `trovati ${list}`, profile: id };
    if (!bestMissing || missing.length < bestMissing.length) bestMissing = missing;
  }
  if (!scanEnded) return st;
  return {
    ...st, status: 'fail', msg: `Sensori interni non rilevati: manca il ${bestMissing.join(' e il ')}`,
    detail: list ? `trovati ${list}` : 'nessun sensore trovato',
  };
}

/** Controllo del profilo (IMU, batteria): con modello ignoto vale il profilo che risponde. */
function evalProfileCheck(id, lines, from, profileId) {
  if (profileId) return evalRuleCheck(R[profileId].checks[id], lines, from);
  for (const p of R.profiles) {
    const st = evalRuleCheck(R[p].checks[id], lines, from);
    if (st.status !== 'pending') return st;
  }
  return { status: 'pending', msg: '', detail: '', at: -1 };
}

function evalCharge(battery) {
  if (battery.status === 'pending') return { status: 'pending', msg: '', detail: '' };
  const v = battery.values?.volts;
  if (battery.status !== 'ok' || !Number.isFinite(v)) return { status: 'na', msg: `Non verificabile: ${DEP_REASON.battery}`, detail: '' };
  const pct = battery.values?.pct;
  const detail = `${Number.isFinite(pct) ? `${fmtNumber(pct)}% · ` : ''}${fmtNumber(v, 3)} V`;
  const b = R.battery;
  if (v < b.plausibleMin || v > b.plausibleMax) return { status: 'warn', msg: 'Lettura batteria anomala', detail };
  if (v < b.okMin) return { status: 'warn', msg: 'Batteria scarica: ricarica la scheda e ripeti il test', detail };
  return { status: 'ok', msg: '', detail };
}

// Se il controllo da cui dipende non è OK, questo non si può verificare; il
// motivo è sempre la causa di partenza (modem guasto → SIM, rete e server).
function applyDependencies(checks) {
  for (const c of CHECKS) {
    const def = C.checks[c.id];
    const dep = def?.dependsOn || (c.id === 'signal' ? 'modem' : null);
    if (!dep) continue;
    const d = checks[dep];
    if (d && (d.status === 'fail' || d.status === 'warn' || d.status === 'na')) {
      const root = d.status === 'na' && d.root ? d.root : dep;
      checks[c.id] = { ...checks[c.id], status: 'na', root, msg: `Non verificabile: ${DEP_REASON[root]}`, detail: '' };
    }
  }
}

/**
 * Stato del test dalle righe ricevute finora. Valuta l'ultimo avvio visto
 * (banner); senza banner valuta tutto quello che è arrivato.
 */
export function evaluate(lines) {
  const start = bootStartIndex(lines);
  const from = Math.max(0, start);
  const info = { model: null, version: null, device: null, server: null, iccid: null, config: null, wakeBoot: null, signal: null };
  const marks = {};
  const mark = (k, i) => { if (marks[k] == null) marks[k] = i; };
  let progress = { pct: 0, label: start >= 0 ? 'Avvio della scheda…' : 'In attesa della scheda…' };
  let alive = 0;
  let fullTracking = -1;
  let firstLoop = -1;
  let sleepAt = -1;
  let summary = null;

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (C.alive.test(l)) alive++;
    if (i < from) continue;
    let m;
    if (!info.model && (m = C.model.exec(l))) info.model = m[1];
    if (!info.version && (m = C.version.exec(l))) info.version = m[1];
    if (!info.device && (m = C.device.exec(l))) info.device = m[1];
    if (!info.server && (m = C.server.exec(l))) info.server = m[1];
    if (!info.config && (m = C.config.exec(l))) info.config = m[1];
    if ((m = C.iccid.exec(l))) info.iccid = m[1];
    if (C.fullTracking.test(l) && fullTracking < 0) fullTracking = i;
    if (fullTracking >= 0 && !summary && (m = C.trackingSummary.exec(l))) summary = { bat: Number(m[1]), sig: Number(m[2]), at: i };
    if (fullTracking >= 0 && firstLoop < 0 && C.loop.test(l)) firstLoop = i;
    if (sleepAt < 0 && C.sleep.test(l)) sleepAt = i;
    for (const p of C.progress) if (p.re.test(l)) progress = { pct: p.pct, label: p.label };
  }
  if (start >= 0) mark('banner', start);

  const profile = resolveProfile(lines, from, info.model);
  const checks = {};
  checks.i2c = evalI2C(lines, from, profile.id);
  if (!profile.id && checks.i2c.profile) { profile.id = checks.i2c.profile; profile.source = 'log'; }
  checks.battery = evalProfileCheck('battery', lines, from, profile.id);
  checks.charge = evalCharge(checks.battery);
  checks.imu = evalProfileCheck('imu', lines, from, profile.id);
  for (const id of ['gnss', 'modem', 'sim', 'network', 'server']) checks[id] = evalRuleCheck(C.checks[id], lines, from);
  if (summary) {
    info.signal = summary.sig;
    checks.signal = summary.sig < R.signalWarnBelow
      ? { status: 'warn', msg: `Segnale debole (${summary.sig}%): prova vicino a una finestra o all'aperto`, detail: `${summary.sig}%` }
      : { status: 'ok', msg: '', detail: `${summary.sig}%` };
  } else {
    checks.signal = { status: 'pending', msg: '', detail: '' };
  }
  if (info.iccid && checks.sim.status === 'ok') checks.sim.detail = `ICCID …${info.iccid.slice(-6)}`;
  checks.led = { status: 'pending', msg: '', detail: '' };
  applyDependencies(checks);

  for (const k of ['modem', 'sim', 'network', 'server']) if (checks[k].status === 'ok') mark(k, checks[k].at);
  if (fullTracking >= 0) mark('fullTracking', fullTracking);
  if (firstLoop >= 0) mark('firstLoop', firstLoop);

  // note, riavvii inattesi, modalità download
  const notes = [];
  const seen = new Set();
  for (let i = 0; i < lines.length; i++) {
    for (const nt of C.notes) {
      if (!seen.has(nt.msg) && nt.re.test(lines[i])) { seen.add(nt.msg); notes.push({ level: nt.level, msg: nt.msg }); }
    }
  }
  // riavvii inattesi: solo con una prova (errore, calo di tensione, watchdog) dopo
  // il primo avvio visto; un reset premuto dal cliente non è un guasto
  let banners = 0;
  let reboot = null;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (C.banner.test(l)) { banners++; continue; }
    if (!banners) continue;
    if (C.reboot.expected.some(re => re.test(l))) reboot = null;
    else if (C.reboot.brownout.some(re => re.test(l))) reboot = 'brownout';
    else if (reboot !== 'brownout' && C.reboot.crash.some(re => re.test(l))) reboot = 'crash';
    else if (!reboot && C.reboot.watchdog.some(re => re.test(l))) reboot = 'watchdog';
  }
  if (reboot === 'brownout') notes.push({ level: 'fail', msg: 'Calo di alimentazione: la scheda si è riavviata da sola (batteria scarica o alimentazione USB insufficiente).' });
  else if (reboot === 'crash') notes.push({ level: 'fail', msg: 'La scheda si è bloccata per un errore interno del firmware.' });
  else if (reboot === 'watchdog') notes.push({ level: 'fail', msg: 'La scheda si è bloccata e si è riavviata da sola (watchdog).' });
  const downloadMode = lines.slice(-20).some(l => C.downloadMode.test(l)) && start < 0;

  // firmware diverso da quello per cui sono scritte le regole
  if (profile.id && info.version && !info.version.startsWith(`${R[profile.id].expectedFirmware}.`)) {
    notes.push({ level: 'info', msg: `Firmware v${info.version}: le regole della pagina sono scritte per la v${R[profile.id].expectedFirmware}.x, alcuni controlli potrebbero restare senza esito.` });
  }
  if (info.model && !R.profiles.includes(info.model)) {
    notes.push({ level: 'info', msg: `Modello ${info.model} non ancora previsto da questa pagina: sensori e batteria si riconoscono dai messaggi.` });
  }

  return {
    bootStart: start,
    booted: start >= 0,
    profile,
    info,
    checks,
    marks,
    progress,
    alive,
    lineCount: lines.length,
    done: fullTracking >= 0 && firstLoop > fullTracking,
    sleeping: sleepAt >= from && start >= 0,
    downloadMode,
    notes,
  };
}

/**
 * Esito finale. reason: 'done' | 'timeout' | 'sleep' | 'nodata' | 'stopped'.
 * led: 'yes' | 'no' | 'unknown' | null (domanda non ancora fatta).
 */
export function finalize(ev, { reason, led = null, anyData = true } = {}) {
  const checks = {};
  for (const c of CHECKS) checks[c.id] = { ...ev.checks[c.id] };

  // comunicazione
  if (!anyData) checks.comm = { status: 'fail', msg: 'La scheda non comunica: controlla il cavo USB e che la scheda sia accesa', detail: '' };
  else if (!ev.alive) checks.comm = { status: 'warn', msg: 'La scheda invia dati illeggibili: riprova o cambia cavo', detail: '' };
  else checks.comm = { status: 'ok', msg: '', detail: ev.booted ? 'avvio completo ricevuto' : 'riceve i messaggi della scheda' };

  for (const c of CHECKS) {
    if (c.id === 'comm' || c.id === 'led' || c.id === 'charge') continue;
    const st = checks[c.id];
    if (st.status !== 'pending') continue;
    if (checks.comm.status !== 'ok') { checks[c.id] = { status: 'na', msg: 'Non verificabile senza comunicazione', detail: '' }; continue; }
    if (!ev.booted) { checks[c.id] = { status: 'na', msg: 'Non osservato: la scheda non si è riavviata durante il test', detail: '' }; continue; }
    if (c.id === 'signal') { checks[c.id] = { status: 'na', msg: 'Non letto', detail: '' }; continue; }
    const t = C.checks[c.id]?.onTimeout;
    checks[c.id] = t
      ? { status: t.status, msg: t.msg, detail: '' }
      : { status: 'fail', msg: reason === 'sleep' ? 'Nessuna risposta prima che la scheda tornasse a riposo' : 'Nessuna risposta', detail: '' };
  }
  if (checks.charge.status === 'pending') checks.charge = evalCharge(checks.battery);
  if (checks.charge.status === 'pending') checks.charge = { status: 'na', msg: `Non verificabile: ${DEP_REASON.battery}`, detail: '' };
  applyDependencies(checks);

  // LED: verde a fine avvio, lo conferma il cliente
  if (!ev.done) checks.led = { status: 'na', msg: 'Si accende di verde solo a fine avvio', detail: '' };
  else if (led === 'yes') checks.led = { status: 'ok', msg: '', detail: 'verde a fine avvio' };
  else if (led === 'no') checks.led = { status: 'warn', msg: 'LED guasto: a fine avvio dovrebbe essere verde', detail: '' };
  else if (led === 'unknown') checks.led = { status: 'na', msg: 'Non verificato', detail: '' };
  else checks.led = { status: 'pending', msg: 'Rispondi alla domanda sul LED', detail: '' };

  const list = CHECKS.map(c => ({ ...c, ...checks[c.id] }));
  const faults = list.filter(c => c.status === 'fail').map(c => c.label);
  const warns = list.filter(c => c.status === 'warn').map(c => `${c.label}${c.msg ? ` — ${c.msg}` : ''}`);
  for (const nt of ev.notes) {
    if (nt.level === 'fail') faults.push(nt.msg);
    else if (nt.level === 'warn') warns.push(nt.msg);
  }

  let verdict;
  if (checks.comm.status === 'fail') verdict = { kind: 'fail', title: 'La scheda non comunica', text: 'Controlla il cavo USB (alcuni cavi caricano soltanto), prova un\'altra presa e verifica che la scheda sia accesa.' };
  else if (faults.length) verdict = { kind: 'fail', title: `Guasto: ${faults.join(', ')}`, text: ev.booted ? 'Copia il log e mandalo all\'assistenza RaceSense.' : 'L\'avvio completo non è stato osservato: ripeti il test premendo il tasto reset quando richiesto, poi manda il log all\'assistenza.' };
  else if (!ev.booted) verdict = { kind: 'incomplete', title: 'Test incompleto', text: 'La scheda non si è riavviata, quindi l\'avvio non è stato controllato. Premi il tasto reset (oppure spegni e riaccendi la scheda) quando la pagina lo chiede, poi ripeti il test.' };
  else verdict = { kind: 'ok', title: 'Scheda funzionante', text: warns.length ? 'Ci sono alcuni punti da verificare, elencati qui sotto.' : 'Tutti i componenti hanno risposto correttamente.' };

  return { checks, list, faults, warns, verdict, reason };
}

export { LABEL as CHECK_LABEL };
